import { useState } from "react";
import { Button } from "@/components/ui/button";

/** Show/hide column picker — shared by Deals and the Opportunity Board. */
export function ColumnsMenu({ allCols, visible, toggle, testIdPrefix = "deals", className = "h-9" }) {
  const [open, setOpen] = useState(false);
  const hidden = allCols.filter(c => !visible.has(c.key)).length;
  return (
    <div className="relative">
      <Button
        onClick={() => setOpen(o => !o)}
        variant="outline"
        className={`${className} rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#E3000B] hover:text-[#E3000B]`}
        data-testid={`${testIdPrefix}-columns-toggle`}
      >
        COLUMNS {hidden > 0 && <span className="ml-1 text-[#E3000B]">({hidden} hidden)</span>}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 z-40 w-56 bg-[#FFFFFF] border border-[#E4E4E0] rounded-sm shadow-lg p-2 space-y-1 max-h-[70vh] overflow-y-auto"
            data-testid={`${testIdPrefix}-columns-menu`}
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 px-2 pb-1 border-b border-[#E4E4E0] mb-1">
              Show / Hide Columns
            </div>
            {allCols.map(c => (
              <label
                key={c.key}
                className="flex items-center gap-2 px-2 py-1 text-xs font-mono text-gray-800 hover:bg-[#F6F6F3] cursor-pointer"
                data-testid={`${testIdPrefix === "deals" ? "" : testIdPrefix + "-"}col-toggle-${c.key}`}
              >
                <input
                  type="checkbox"
                  checked={visible.has(c.key)}
                  onChange={() => toggle(c.key)}
                  className="accent-[#E3000B] w-3 h-3"
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** localStorage-backed visible-column set + toggle helper. */
export function useVisibleCols(storageKey, allCols) {
  const [visible, setVisible] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (Array.isArray(saved)) return new Set(saved);
    } catch {}
    return new Set(allCols.map(c => c.key));
  });
  const toggle = (key) => setVisible(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
    return next;
  });
  return [visible, toggle, (k) => visible.has(k)];
}
